import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
/**
 * Platform-safe wrapper around the browser's localStorage API that no-ops gracefully
 * in server-side rendering contexts where localStorage is unavailable.
 */
export class LocalStorageService {

  constructor(@Inject(PLATFORM_ID) private platformId: Object) { }

  /** Clears all entries from local storage (no-op in non-browser environments). */
  clear(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.clear();
    }
  }

  /**
   * Stores a string value in local storage under the given key.
   * @param key The storage key to write.
   * @param value The string value to store.
   */
  setItem(key: string, value: string): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(key, value);
    }
  }

  /**
   * Retrieves a string value from local storage by key.
   * @param key The storage key to read.
   * @returns The stored string, or `null` if absent or in a non-browser environment.
   */
  getItem(key: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem(key);
    }
    return null;
  }

  /**
   * Removes the entry with the given key from local storage.
   * @param key The storage key to delete.
   */
  removeItem(key: string): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(key);
    }
  }

  /**
   * Returns the key name at the given index in local storage.
   * @param index The zero-based index of the key.
   * @returns The key string at that index, or `null` if out of range or non-browser.
   */
  key(index: number): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.key(index);
    }
    return null;
  }

  get length(): number {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.length;
    }
    return 0;
  }
}
